import {
  listPlatformInvitations,
  listPlatformSuperAdmins,
  resendPlatformInvitation,
} from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type { InvitationListItem, PlatformSuperAdminListItem } from '@jq4race/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@jq4race/ui';
import { CheckCircle2, Plus, TriangleAlert, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { AccessDeniedState } from '../../../../shared/errors/AccessDeniedState';
import { isForbiddenError } from '../../../../shared/errors/isForbiddenError';
import { RestrictedMeta, restrictedCount } from '../../../../shared/errors/RestrictedMeta';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { useAutoDismissFeedback } from '../../../../shared/hooks/useAutoDismissFeedback';
import { PageHeader } from '../../../app-shell/ui/PageHeader';
import { useAuthState } from '../../../auth/ui/auth-state-context';
import { InvitePlatformAdminDialog } from '../../../organizations/ui/components/InvitePlatformAdminDialog';
import { InvitationsTable } from '../components/InvitationsTable';
import { PlatformAdminsTable } from '../components/PlatformAdminsTable';
import { ToggleAdminActiveDialog } from '../components/ToggleAdminActiveDialog';

export function PlatformTeamPage() {
  const { t } = useTranslation('users');
  const { session } = useAuthState();
  const currentUserId = session?.user.id ?? null;
  const [members, setMembers] = useState<PlatformSuperAdminListItem[]>([]);
  const [invitations, setInvitations] = useState<InvitationListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // See EventsPage.tsx for the confirmed production incident this guards
  // against: a 403 (role changed mid-session) must not render like "no
  // admins", and its banner must not auto-dismiss.
  const [accessDenied, setAccessDenied] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [toggleTarget, setToggleTarget] = useState<PlatformSuperAdminListItem | null>(null);
  const [tab, setTab] = useState<'members' | 'invitations'>('members');

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAccessDenied(false);
    try {
      const [m, i] = await Promise.all([listPlatformSuperAdmins(), listPlatformInvitations()]);
      setMembers(m.members);
      setInvitations(i.invitations);
    } catch (e) {
      setAccessDenied(isForbiddenError(e));
      setError(translateApiError(e, t, { fallback: t('platformTeam.loadError') }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useAutoDismissFeedback({
    error,
    success,
    isErrorPersistent: accessDenied,
    onDismissError: () => setError(null),
    onDismissSuccess: () => setSuccess(null),
  });

  function handleInvited(email: string) {
    setSuccess(t('platformTeam.invitationSent', { email }));
    setTab('invitations');
    void reload();
  }

  async function handleResend(invitationId: string) {
    setError(null);
    setSuccess(null);
    try {
      const result = await resendPlatformInvitation(invitationId);
      setSuccess(t('platformTeam.invitationResent', { email: result.email }));
      await reload();
    } catch (e) {
      setError(translateApiError(e, t, { fallback: t('platformTeam.resendError') }));
    }
  }

  function handleToggled(
    member: PlatformSuperAdminListItem,
    active: boolean,
    sessionsRevoked: number,
  ) {
    setSuccess(
      active
        ? t('platformTeam.adminReactivated', { email: member.email })
        : t('platformTeam.adminDeactivated', { email: member.email, count: sessionsRevoked }),
    );
    void reload();
  }

  const activeCount = members.filter((m) => m.active).length;
  const pendingInvites = invitations.filter((i) => i.status === 'pending').length;

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('platformTeam.eyebrow')}
        title={t('platformTeam.title')}
        description={t('platformTeam.description')}
        meta={
          <HeaderStats
            loading={loading && members.length === 0}
            accessDenied={accessDenied}
            stats={[
              { value: activeCount, label: t('platformTeam.stats.admin', { count: activeCount }) },
              {
                value: pendingInvites,
                label: t('platformTeam.stats.pendingInvite', { count: pendingInvites }),
              },
            ]}
          />
        }
        actions={
          <Button size="sm" onClick={() => setInviteOpen(true)} disabled={loading}>
            <Plus className="h-4 w-4" strokeWidth={1.75} />
            {t('platformTeam.inviteAdmin')}
          </Button>
        }
      />

      <FeedbackBanner tone="error" message={error} onDismiss={() => setError(null)} />
      <FeedbackBanner tone="success" message={success} onDismiss={() => setSuccess(null)} />

      <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
        <TabsList>
          <TabsTrigger value="members">
            {t('platformTeam.tabs.members')}
            <Badge variant="secondary" className="ml-1 px-2 text-[11px]">
              {restrictedCount(accessDenied, activeCount)}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="invitations">
            {t('platformTeam.tabs.invitations')}
            <Badge variant="secondary" className="ml-1 px-2 text-[11px]">
              {restrictedCount(accessDenied, pendingInvites)}
            </Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="members">
          <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
            <CardContent className="p-6 sm:p-8">
              {accessDenied ? (
                <AccessDeniedState />
              ) : (
                <PlatformAdminsTable
                  members={members}
                  loading={loading}
                  currentUserId={currentUserId}
                  onToggleActive={setToggleTarget}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invitations">
          <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
            <CardContent className="p-6 sm:p-8">
              {accessDenied ? (
                <AccessDeniedState />
              ) : (
                <InvitationsTable
                  invitations={invitations}
                  loading={loading}
                  onResend={handleResend}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <InvitePlatformAdminDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        onInvited={handleInvited}
      />

      <ToggleAdminActiveDialog
        member={toggleTarget}
        onClose={() => setToggleTarget(null)}
        onToggled={handleToggled}
      />
    </div>
  );
}

interface FeedbackBannerProps {
  tone: 'error' | 'success';
  message: string | null;
  onDismiss: () => void;
}

interface HeaderStat {
  value: number;
  label: string;
}

function HeaderStats({
  stats,
  loading,
  accessDenied,
}: {
  stats: HeaderStat[];
  loading: boolean;
  accessDenied: boolean;
}) {
  return (
    <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-jq-graphite">
      <RestrictedMeta loading={false} accessDenied={accessDenied}>
        {stats.map((stat, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: stats are a fixed positional list
          <span key={`stat-${index}`} className="inline-flex items-center gap-3">
            {index > 0 && (
              <span aria-hidden className="text-jq-stone">
                ·
              </span>
            )}
            <span>
              {loading ? (
                <Skeleton className="inline-block h-[14px] w-6 align-middle" />
              ) : (
                <span className="font-medium tabular-nums text-jq-charcoal">{stat.value}</span>
              )}{' '}
              {stat.label}
            </span>
          </span>
        ))}
      </RestrictedMeta>
    </p>
  );
}

function FeedbackBanner({ tone, message, onDismiss }: FeedbackBannerProps) {
  const { t } = useTranslation('users');
  if (!message) return null;
  const Icon = tone === 'success' ? CheckCircle2 : TriangleAlert;
  const palette =
    tone === 'success'
      ? 'border-jq-success/40 bg-jq-success/10 text-jq-success'
      : 'border-jq-danger/40 bg-jq-danger/10 text-jq-danger';
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-sm border px-4 py-3 text-[13px] ${palette}`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('platformTeam.dismissMessage')}
        className="-mr-1 -mt-1 rounded p-1 opacity-70 transition hover:opacity-100"
      >
        <X className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}
